import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import type {
  CreateSiteRequest,
  DeviceCatalogEntry,
  FlagState,
  ImportRow,
  Site,
  SiteType,
  VendorDirectoryEntry,
  VesselType,
} from '@mvcp/contracts';
import { normalizeDeviceCategory } from './device-category';

/**
 * Framework-free (no React) parse + classify module for the bulk-import wizard
 * (Story 2.7, FR-27). It reads the Manager's OWN file in the browser into a
 * canonical {@link ImportRow}[]; `classifyImport` auto-matches each distinct
 * Site/Vendor/Device against the caller's visible global Directory (exact,
 * case-insensitive) and surfaces matched / unmatched / ambiguous entities plus
 * row errors. The server never sees file bytes — the API stays pure JSON.
 *
 * Story 7.4 split the parse half into THREE primitives so a Manager can confirm
 * (and correct) the header→field resolution instead of having it decided
 * invisibly by an alias table:
 *
 *   1. {@link readImportFile}  file  → `{ columns, records }` — the file's own
 *      headers and its cells with no field resolution applied (the one exception
 *      is a non-object entry, which becomes `{}` so the row still counts)
 *   2. {@link suggestMapping}  columns → a suggested {@link ImportColumnMapping}
 *   3. {@link applyMapping}    records + mapping → canonical {@link ImportRow}[]
 *
 * {@link HEADER_ALIASES} is now the *suggestion engine* only; the CONFIRMED
 * mapping is what parses the rows. {@link parseImportFile} is retained as the
 * thin `read → suggest → apply` wrapper — the default (nobody-touched-a-control)
 * path — which is what makes every pre-existing parse test a regression guard.
 *
 * Story 8.3 gave the first primitive an explicit column identity ({@link
 * ImportColumn}, AD-23): every physical column is allocated a synthetic key, its
 * verbatim header and its 1-based position AS THE FILE IS READ, and the records,
 * the mapping and the samples are keyed by that key. Nothing downstream infers a
 * column list from a record's `Object.keys`, and no parser-internal token
 * (`Version_1`, `__EMPTY`) can be an identity or a display name. XLSX cells are
 * read as the text the sheet DISPLAYS, so `1.10` and a date arrive as the Manager
 * sees them rather than as `1.1` and a serial number.
 *
 * SITE_TYPES / FLAG_STATES / VESSEL_TYPES and the IMO format are inlined here
 * (web imports @mvcp/contracts TYPES ONLY — importing the runtime arrays breaks
 * `vite build`: CJS __exportStar, Story 1.6 debug log). Keep in sync with
 * `packages/contracts/src/entities.ts`.
 */
// Exported so `enum-sync.test.ts` asserts they stay byte-equal to the contract
// runtime arrays (the copy has no compile-time guard; the test is the guard).
export const SITE_TYPES = ['ship', 'office'] as const;
export const FLAG_STATES = [
  'Panama', 'Liberia', 'Marshall Islands', 'Hong Kong', 'Singapore', 'Malta',
  'Bahamas', 'Greece', 'Cyprus', 'China', 'Japan', 'Norway', 'Denmark', 'Italy',
  'United Kingdom', 'Germany', 'Netherlands',
] as const;
export const VESSEL_TYPES = [
  'Crude Oil Tanker', 'LNG Carrier', 'Bulk Carrier', 'Container', 'Chemical Tanker',
] as const;
const IMO_RE = /^\d{7}$/;

/** Lower-case → canonical casing, so `panama` / `lng carrier` normalize to the
 * contract's exact value (which the server's isFlagState/isVesselType demand). */
const FLAG_BY_LOWER = new Map(FLAG_STATES.map((f) => [f.toLowerCase(), f as string]));
const VESSEL_BY_LOWER = new Map(VESSEL_TYPES.map((v) => [v.toLowerCase(), v as string]));

/**
 * Guards on untrusted spreadsheet bytes (xlsx@0.18.5 carries
 * ReDoS/prototype-pollution advisories): a hard file-size ceiling, a parsed-row
 * ceiling and a column ceiling. Well within the epic's scale (tens of Sites, low
 * hundreds of Devices); an over-cap file is refused with a clear inline error.
 *
 * Only the SIZE ceiling runs before parsing — it is the one that bounds what the
 * parser is handed. The row and column ceilings are counted from the parse
 * output, so they bound what the WIZARD is handed, not what xlsx chews through.
 *
 * {@link MAX_COLUMNS} is INTENDED, AUTHORIZED behaviour — Story 8.3 (AC5) decided
 * to keep it, so the code and the intent agree here rather than only in a spec.
 * Do not "restore" the un-capped behaviour: the mapping step renders a row, a
 * position disclosure and a 13-option `<select>` PER COLUMN, so column count is
 * what drives the DOM, and the row cap does not bound a wide file — a 5 MB
 * single-row CSV can carry 200,000 headers and clear both the size and row guards.
 * What the cap therefore prevents is precisely a RENDER the Manager cannot escape;
 * it is not a bound on the parse, which has already allocated every column and
 * materialised every record by the time this check runs (the size ceiling is the
 * only pre-parse guard, for CSV and XLSX alike). 500 columns is far outside the
 * epic's documented scale (tens of Sites, low hundreds of Devices), so the ceiling
 * costs no real file anything.
 *
 * (The cap was originally added by a Story 7.4 review pass without intent
 * authority, which was the legitimate complaint; Story 8.3 retires it by
 * authorizing the behaviour rather than by removing a real protection.)
 */
const MAX_FILE_BYTES = 5 * 1024 * 1024; // 5 MB
const MAX_ROWS = 5000;
const MAX_COLUMNS = 500;

/**
 * Header-alias map → canonical {@link ImportRow} keys. Keys are the incoming
 * header trimmed + lower-cased, so `Site Name`, `site_name`, and `siteName` all
 * normalize to `siteName`.
 *
 * EXPORTED for one reason only (Story 8.4): the recognised-columns help is derived
 * from it ({@link HEADER_ALIAS_HELP}), and a coverage test that reads the derivation
 * alone would only compare it with itself — narrowing the derivation's own filter left
 * every such assertion green while the help rendered "no recognized header" for two
 * fields that have several. The suites assert `Object.keys(HEADER_ALIASES)` is covered
 * by what the help RENDERS, which is a claim about the table. Do not resolve a header
 * through this map anywhere new: {@link suggestMapping} is the only resolver.
 */
export const HEADER_ALIASES: Readonly<Record<string, keyof ImportRow>> = {
  'site name': 'siteName', 'site_name': 'siteName', 'sitename': 'siteName',
  'type': 'siteType', 'site type': 'siteType', 'sitetype': 'siteType',
  'imo': 'imo',
  'flag': 'flagState', 'flag state': 'flagState', 'flagstate': 'flagState',
  'vessel type': 'vesselType', 'vesseltype': 'vesselType',
  'city': 'city',
  'vendor': 'vendorName', 'vendor name': 'vendorName', 'vendorname': 'vendorName',
  'vendor country': 'vendorCountry', 'vendorcountry': 'vendorCountry',
  'vendor category': 'vendorCategory', 'vendorcategory': 'vendorCategory',
  'device': 'deviceModel', 'device model': 'deviceModel', 'devicemodel': 'deviceModel',
  'device category': 'deviceCategory', 'devicecategory': 'deviceCategory',
  // Story 7.1 / 8.4 — the device hardware/firmware revision. The field is called a
  // "hardware/firmware revision" in the contract, the migration prose and every
  // spec that touches it, so `Firmware`, `FW`, `Rev` and `Revision` are at least as
  // likely a real column heading as `Version` (Story 8.4). All five unqualified
  // spellings are WEAK aliases: see WEAK_HEADER_ALIASES below. They are the aliases
  // most likely to be a misread — a sheet's own document/template revision — so
  // whenever the file also carries a qualified `Device Version`, that one must win,
  // and must win regardless of which column comes first.
  //
  // `device_version` is here because a machine-exported sheet emits the underscore
  // spelling. The map is NOT uniformly underscore-complete — only `site_name`
  // predates this — and it never claimed to be: the wizard's recognised-columns help
  // is GENERATED from this table ({@link HEADER_ALIAS_HELP}, Story 8.4 AC3), so it
  // describes exactly the spellings that exist and a new alias here is documented on
  // screen with no help-text edit at all.
  'device version': 'deviceVersion', 'device_version': 'deviceVersion',
  'deviceversion': 'deviceVersion',
  'version': 'deviceVersion',
  'firmware': 'deviceVersion', 'fw': 'deviceVersion',
  'rev': 'deviceVersion', 'revision': 'deviceVersion',
};

/**
 * Headers demoted to WEAK: they name a field without naming the entity it belongs
 * to, so they are the aliases most likely to be a coincidence. A weak header only
 * FILLS a canonical field that nothing else supplied; a qualified header always
 * wins, whichever column the spreadsheet lists first. Without this the winner
 * would be object-iteration order, i.e. nondeterministic from the user's point of
 * view.
 *
 * Membership is by CRITERION, not by field (Story 8.4 widened it from `version`
 * alone): an alias belongs here when it names the device REVISION without naming
 * the entity, so that its likely misread — a sheet's own document, template or
 * schema revision — would stamp a fabricated fact onto a shared-backbone
 * `device.version` that no tenant can later correct (migration 014/030: a tenant
 * `UPDATE` on `device` matches zero rows, silently). That is the whole test, and
 * `version`, `firmware`, `fw`, `rev` and `revision` all meet it — `rev` most
 * sharply, since it collides outright with a document revision.
 *
 * It is NOT the full set of unqualified aliases: `vendor`, `device`, `type`,
 * `flag`, `city` and `imo` are equally unqualified and stay in the ordinary
 * (non-weak) group, because a misread there produces a wrong value on the
 * IMPORTING tenant's own overlay row (or a visible row error), not a fabricated
 * fact on a row every other tenant reads.
 *
 * Demotion costs nothing: a weak alias still FILLS a field nothing else supplies,
 * so `Firmware` alone is read exactly as `Device Version` alone would be. What it
 * buys is that a file carrying BOTH cannot let the coincidence win. (`firmware` is
 * arguably qualified enough to be ordinary; splitting the four across two
 * precedence classes would mean documenting a distinction no reader could predict,
 * so they share one.)
 *
 * **KNOWN BOUNDARY, logged in `deferred-work.md` (Story 8.4).** Weakness only bites
 * when a qualified column ALSO exists, so it does not neutralise the misread — it
 * ORDERS it. A fleet export carrying a `Rev`/`Revision` column that means the
 * DOCUMENT revision and no `Device Version` column is now pre-mapped to
 * `deviceVersion` and, if confirmed, writes that value to a shared-backbone
 * `device.version` no tenant can correct; before Story 8.4 the column was ignored and
 * the value simply dropped. The AC commands recognition and the ledger sanctioned it
 * once Story 7.4's confirm step existed — the column, its sample and its target are
 * all on screen before Continue, and the review step shows the parsed version — so the
 * behaviour is intended. What changed is the DIRECTION of the hazard, which is
 * recorded rather than left to be rediscovered.
 *
 * **WEAK vs WEAK.** Widening the set from one member to five created a case that could
 * not previously arise: two weak columns targeting the same field with no qualified
 * column present. {@link mappedColumnsByField} appends the weak bucket in COLUMN order,
 * so the left-most weak column is consulted first and `classifyImport`'s first-non-empty
 * rule takes its value — `…,Rev,Firmware` resolves to the `Rev` value, `…,Firmware,Rev`
 * to the `Firmware` one. That is deterministic and it is the same file-order rule AD-24
 * states everywhere else, so it is the right tie-break; what it is NOT is a SAFE one.
 * The strictly worse sibling of the boundary above lands here: a sheet carrying a `Rev`
 * column meaning the document revision AND a `Firmware` column holding the real firmware
 * resolves on column position alone, so the fabricated fact wins whenever the document
 * revision happens to sit to the left. Weakness cannot order weakness. Pinned in
 * `import-parse.test.ts` (both orders) so the tie-break cannot change unobserved, and
 * logged in `deferred-work.md` — the only real fix is qualifying the alias, which the AC
 * does not authorise here.
 *
 * Story 7.4 CHANGED how the non-weak group resolves. Two ordinary aliases hitting
 * one field (`Vendor` + `Vendor Name`, `Type` + `Site Type`, `Device` + `Device
 * Model`) used to be last-writer-wins by object-iteration order; they are now
 * FIRST NON-EMPTY in file order, and the mapping step discloses which column
 * wins. Both changes are deliberate — the old rule was an undisclosed wart, and
 * an invisible winner is exactly what this story exists to remove — but it IS a
 * behaviour change for such files: the value that reaches `classifyImport` (which
 * keys vendors on the lower-cased name) can differ, so a sheet whose two vendor
 * columns disagree may now match a different existing Vendor than it did before.
 * `import-parse.test.ts` pins the new winner for `Vendor`/`Vendor Name` and
 * `Type`/`Site Type`.
 *
 * Membership is by incoming header (trimmed + lower-cased), matching
 * {@link HEADER_ALIASES} keys. Demoting a further alias needs only an entry here —
 * the precedence loop does not change.
 */
const WEAK_HEADER_ALIASES = new Set<string>([
  'version', 'firmware', 'fw', 'rev', 'revision',
]);

// ---- Column-mapping vocabulary (Story 7.4) --------------------------------

/** A canonical {@link ImportRow} key — i.e. a mapping target. */
export type ImportField = keyof ImportRow;

/** One selectable target in the wizard's Map-columns step. */
export interface ImportFieldSpec {
  field: ImportField;
  /** The human label; also the `<option>` text. */
  label: string;
  /** `siteName` only — every row without it is a "missing site name" row error. */
  required?: boolean;
}

/**
 * The mapping step's target list: a 1:1 cover of {@link ImportRow}, in the order
 * the Manager reads them (Site → Vendor → Device). Rendering the select from
 * DATA rather than JSX is what keeps the list from drifting from the row model —
 * a new `ImportRow` field is added here once and appears in the control.
 */
const IMPORT_FIELD_TUPLE = [
  { field: 'siteName', label: 'Site Name', required: true },
  { field: 'siteType', label: 'Type' },
  { field: 'imo', label: 'IMO' },
  { field: 'flagState', label: 'Flag' },
  { field: 'vesselType', label: 'Vessel Type' },
  { field: 'city', label: 'City' },
  { field: 'vendorName', label: 'Vendor' },
  { field: 'vendorCountry', label: 'Vendor Country' },
  { field: 'vendorCategory', label: 'Vendor Category' },
  { field: 'deviceModel', label: 'Device' },
  { field: 'deviceCategory', label: 'Device Category' },
  { field: 'deviceVersion', label: 'Device Version' },
] as const satisfies readonly ImportFieldSpec[];

// Widened for rendering (`required` reads as `boolean | undefined` on every
// entry); the literal tuple above stays for the cover check below.
export const IMPORT_FIELDS: readonly ImportFieldSpec[] = IMPORT_FIELD_TUPLE;

/** The label shown for a column the Manager is NOT importing. */
export const IGNORE_LABEL = '— ignore —';

// Compile-time proof the list above covers EVERY ImportRow key. An added field
// with no entry here makes `Exclude<…>` a non-never union, which fails to
// satisfy the `extends never` parameter — a type error, not a dropped column.
// (The `satisfies` above catches the other direction: a stale/renamed entry.)
type _Never<T extends never> = T;
type _ImportFieldsCoverImportRow = _Never<
  Exclude<ImportField, (typeof IMPORT_FIELD_TUPLE)[number]['field']>
>;

/** The three headings the mapping targets read under (Site → Vendor → Device). */
export type ImportFieldGroup = 'Sites' | 'Vendors' | 'Devices';

/** One field's row in the recognised-columns help. */
export interface FieldAliasHelp {
  field: ImportField;
  /** {@link ImportFieldSpec.label} — the same word the mapping `<select>` shows. */
  label: string;
  required: boolean;
  group: ImportFieldGroup;
  /**
   * EVERY recognised spelling for this field, verbatim as {@link HEADER_ALIASES}
   * holds them (trimmed + lower-cased), in table order. Rendered as-is rather than
   * title-cased, because these are the exact strings matching is performed on and
   * matching ignores case — a prettified copy would be a second, drifting list.
   */
  aliases: readonly string[];
  /**
   * The subset of {@link aliases} in `WEAK_HEADER_ALIASES` — spellings that are
   * consulted only after every qualified one. Split out so the help can SAY so:
   * rendering all eight device-version spellings as equals tells the Manager that
   * `Rev` and `Device Version` are interchangeable, which is exactly the precedence
   * the weak set exists to deny.
   */
  weakAliases: readonly string[];
  /**
   * What a row must ALSO carry once this field is mapped, when the requirement is
   * conditional and stated nowhere else. Only the two site-type-conditional cases
   * have one; the alias table cannot express it, and both are live exclusion reasons
   * (`ship is missing a flag state`, `office is missing a city`) whose absence from
   * the help is what let the hand-written list's "for ships: … for offices: …"
   * disappear when the list became generated.
   */
  note?: string;
}

/**
 * The recognised-columns help, DERIVED from {@link HEADER_ALIASES} (Story 8.4 AC3).
 *
 * The wizard's `<details>` used to carry a hand-picked subset of the alias table —
 * it named `Site Name`, `Type`, `Vendor`, `Device Version` (or `Version`) and
 * omitted `Site Type`, `Vendor Name`, `Device Model`, `Flag State`, `site_name`,
 * `sitename`, … — so the one surface whose job is to describe the alias table
 * described a different, smaller one. Rewriting that list by hand would satisfy the
 * AC for exactly as long as the next alias takes to land; generating it makes
 * "describe the alias table that exists" true BY CONSTRUCTION, and a future alias
 * appears on screen with no help edit at all (pinned by a coverage test asserting
 * every key of the table is reachable in the rendered help).
 *
 * Ordered by {@link IMPORT_FIELDS} — the order the Manager reads the mapping
 * controls in — and grouped by the field key's own prefix, so a new `ImportRow`
 * field lands in a group mechanically instead of needing a hand-maintained lookup
 * that could silently drop it out of the help entirely.
 *
 * A field with no alias at all is still listed (with an empty `aliases`): "this
 * field exists but nothing is guessed for it" is exactly what the Manager needs to
 * know, and omitting the row would hide the field from the help.
 *
 * `required` is per FIELD and only `siteName` carries it, so the help must not label a
 * GROUP "optional": every other Site field is conditionally required (a ship needs its
 * IMO, flag and vessel type; an office needs its city) — see {@link FieldAliasHelp.note}.
 *
 * Every field whose absence is a row-EXCLUSION reason in {@link validateImportRow} must
 * carry either `required` or a note here, or the help renders it indistinguishably from a
 * genuinely optional one. `siteType` is the sharp case: it is not flagged `required` in
 * {@link IMPORT_FIELDS} (only `siteName` is), yet a blank Type excludes EVERY row via
 * `site-type-missing`. `deviceModel` likewise — `device-without-vendor` excludes the row
 * when a model arrives with no vendor name.
 */
const CONDITIONAL_NOTES: Partial<Record<ImportField, string>> = {
  siteType: 'required on every row',
  imo: 'required on a ship row',
  flagState: 'required on a ship row',
  vesselType: 'required on a ship row',
  city: 'required on an office row',
  deviceModel: 'needs a Vendor column too',
};

export const HEADER_ALIAS_HELP: readonly FieldAliasHelp[] = IMPORT_FIELDS.map((spec) => {
  const aliases = Object.keys(HEADER_ALIASES).filter(
    (alias) => HEADER_ALIASES[alias] === spec.field,
  );
  const note = CONDITIONAL_NOTES[spec.field];
  return {
    field: spec.field,
    label: spec.label,
    required: spec.required === true,
    group: spec.field.startsWith('vendor')
      ? 'Vendors'
      : spec.field.startsWith('device')
        ? 'Devices'
        : 'Sites',
    aliases,
    weakAliases: aliases.filter((alias) => WEAK_HEADER_ALIASES.has(alias)),
    ...(note ? { note } : {}),
  };
});

/**
 * A confirmed column→field mapping, keyed by {@link ImportColumn.key} — the
 * column's synthetic identity, NOT its header text. `''` means "— ignore —": the
 * column is disclosed in the wizard and contributes to no row. A column absent
 * from the map is treated the same as `''`.
 */
export type ImportColumnMapping = Record<string, ImportField | ''>;

/**
 * ONE physical column of the read file, with the identity every consumer keys on
 * (Story 8.3). Allocated positionally as the file is read, so nothing downstream
 * has to reconstruct a column list from a record's `Object.keys`.
 *
 * - `key` is OPAQUE and is never rendered, never aliased, never shown to a human.
 *   It is what {@link ParsedImportFile.records}, {@link ImportColumnMapping} and
 *   {@link columnSamples} are keyed by. For CSV/XLSX it is synthetic (`c0`, `c1`,
 *   …), which is also what makes a column headed `__proto__` genuinely readable:
 *   the record never carries a prototype-named key at all.
 * - `header` is the VERBATIM file text (`''` when the file gave the column no
 *   header). It is the only thing ever resolved through {@link HEADER_ALIASES},
 *   the only thing bucketed as weak-vs-qualified, and the only thing displayed.
 * - `position` is the 1-based file order, and is the ONLY source of a
 *   `(column N)` label. It is the column number the Manager can point at in their
 *   own file for a single-sheet XLSX and for a CSV — including a sheet laid out
 *   from column B, whose first column is 2, not 1. For a multi-sheet workbook it is
 *   the position in the UNIONED list (logged in `deferred-work.md`). A phantom
 *   padding column dropped at read time leaves a GAP rather than renumbering the
 *   file, so `position` is not the list index.
 *
 * All three are `readonly`: an instance is allocated once and shared between
 * `columns`, the read-time registry and each sheet's own column list, so the
 * compiler — not a docblock — is what keeps an identity from being edited in place.
 *
 * No parser-internal token (`Version_1`, `__EMPTY`, `_1`, `Vendor_1`) can be a
 * key, a header or a display name any more: the parser is never asked to name a
 * column, so it has no collisions to resolve.
 */
export interface ImportColumn {
  readonly key: string;
  readonly header: string;
  readonly position: number;
}

/** A file read into raw records plus the columns actually detected in it. */
export interface ParsedImportFile {
  /**
   * Every PHYSICAL column of the file, in true file order, exactly once —
   * duplicated, blank or ordinary. Two columns headed `Vendor` are two columns.
   *
   * ONE subtraction, XLSX only: a column that is blank-headed AND holds no value on
   * any row is `!ref` padding rather than a column (see `readImportFile`), so it is
   * not listed. It is XLSX-only because papaparse reports a real physical header
   * count while a sheet's stored used range is an editing artifact — a CSV header
   * row ending in a comma therefore still lists its headerless column. Surviving
   * columns keep their true {@link ImportColumn.position}, so a dropped phantom
   * leaves a GAP in the positions rather than renumbering the file.
   *
   * The list is built ONCE, as the file is read, by allocating an
   * {@link ImportColumn} per physical column (CSV: per `transformHeader` index;
   * XLSX: per cell of each sheet's header row, keyed workbook-wide so a header
   * shared by two sheets stays ONE column). There is no ordering *hint* and no
   * reconciliation of a header list against output keys — the two can no longer
   * disagree, which is what closes the renamed-duplicate, misordered-blank,
   * internal-token and unreadable-`__proto__` defects together (Story 8.3, AD-23).
   *
   * Consequently `Object.keys` orders nothing: an integer-like header (`2024`)
   * keeps its physical position instead of being hoisted by key iteration.
   *
   * JSON is the one exception, and deliberately so: it has no header row, so its
   * columns ARE its record keys in first-seen order and `key === header`. That is
   * why {@link mappingTarget}'s own-property guard is still load-bearing — a JSON
   * key may legitimately be `__proto__` / `constructor` / `toString`, so a column
   * identity can still collide with `Object.prototype`. Do not "simplify" the
   * guard away on the grounds that CSV/XLSX keys are synthetic.
   */
  columns: readonly ImportColumn[];
  /**
   * The raw rows, keyed by {@link ImportColumn.key}. Untyped — nothing is
   * resolved yet. XLSX rows are built on `Object.create(null)`.
   */
  records: Record<string, unknown>[];
}

/**
 * The name shown for a column the file gave no header of its own. Deliberately not
 * exported: {@link columnLabels} is the only thing that may name a column, and the
 * suites assert the literal a Manager reads rather than importing the constant that
 * produces it (a test sharing the constant would pass if both changed together).
 */
const UNNAMED_COLUMN_LABEL = '(unnamed column)';

/** What the mapping step calls each column, and whether the file left any unclear. */
export interface ColumnLabels {
  /** The display name per {@link ImportColumn.key}. */
  names: Map<string, string>;
  /**
   * True when at least one column has no header of its own or shares one with
   * another column — i.e. when some name below carries a `(column N)`. The
   * wizard's one honest disclosure: the parser renames nothing, so there is no
   * rename to announce; what the Manager needs to know is that the FILE left some
   * columns without a distinct name.
   */
  anyIndistinct: boolean;
}

/**
 * What to CALL each column in the UI, keyed by {@link ImportColumn.key}, plus
 * whether the file left any column indistinct — ONE computation, because both
 * answers come from the same collision count. Computing them separately is how the
 * label and the sentence explaining it drift apart.
 *
 * A column may have no header of its own — a trailing comma in a CSV header row, a
 * blank header cell in a sheet — and rendering that verbatim gives an empty
 * `<strong>` and a `<select>` with no accessible name, so it is named
 * {@link UNNAMED_COLUMN_LABEL}. That applies to every format: it used to be
 * CSV-only in effect, because the XLSX parser replaced a blank header with an
 * internal token (`__EMPTY`, `_1`) that is not blank — so the Manager was shown a
 * name they could not find anywhere in their file.
 *
 * Names must also be UNIQUE, which headers are not: two blank ones are both
 * `(unnamed column)`, two columns genuinely headed `Vendor` are two columns, and
 * `Site Name` vs `Site Name ` differ only in whitespace, which accessible-name
 * computation collapses. Either way two `<select>`s would share one accessible name
 * and the duplicate-winner disclosure would name the winner and the loser
 * identically. So a name gains its POSITION when it collides — and always when the
 * column has no header of its own, because `(unnamed column)` names nothing the
 * Manager can locate and the position is the only thing that does.
 *
 * The position is `column.position`, recorded as the file was read (AD-23 rule 5),
 * NEVER this array's index. The index was the bug: it printed an
 * authoritative-looking `(column N)` pointing at the wrong spreadsheet column
 * whenever the parser reordered a renamed key. The two coincide for most files but
 * not all — a phantom padding column dropped at read time leaves a gap — so the
 * distinction is live, not historical.
 *
 * Lives here rather than in the wizard because it is a property of the column
 * model, not of the React tree (and `IMPORT_FIELDS`' labels and
 * {@link IGNORE_LABEL} already put this module's UI copy in one place).
 */
export function columnLabels(columns: readonly ImportColumn[]): ColumnLabels {
  const base = columns.map((c) => (c.header.trim() === '' ? UNNAMED_COLUMN_LABEL : c.header));
  // Collision is judged on the ACCESSIBLE name, which collapses whitespace and is
  // matched case-insensitively by most assistive tech and by RTL's queries.
  const nameKey = (name: string): string => name.trim().replace(/\s+/g, ' ').toLowerCase();
  const counts = new Map<string, number>();
  for (const name of base) counts.set(nameKey(name), (counts.get(nameKey(name)) ?? 0) + 1);

  const names = new Map<string, string>();
  let anyIndistinct = false;
  columns.forEach((column, i) => {
    const name = base[i] ?? '';
    const indistinct = column.header.trim() === '' || (counts.get(nameKey(name)) ?? 0) > 1;
    if (indistinct) anyIndistinct = true;
    names.set(column.key, indistinct ? `${name} (column ${column.position})` : name);
  });
  return { names, anyIndistinct };
}

/**
 * Read a File's text. Prefers the native `Blob.text()` (browsers) and falls back
 * to `FileReader` (jsdom, whose File lacks the promise-based methods) so the same
 * code path works in the app and under test.
 */
function readText(file: File): Promise<string> {
  if (typeof file.text === 'function') return file.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the file.'));
    reader.readAsText(file);
  });
}

/** Read a File's bytes. Same native-then-FileReader fallback as {@link readText}. */
function readArrayBuffer(file: File): Promise<ArrayBuffer> {
  if (typeof file.arrayBuffer === 'function') return file.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the file.'));
    reader.readAsArrayBuffer(file);
  });
}

/**
 * The synthetic, opaque {@link ImportColumn.key} for the nth column allocated.
 * Never rendered — see {@link ImportColumn}.
 */
function columnKey(index: number): string {
  return `c${index}`;
}

/**
 * Excel's closed set of error codes, for the rare error cell that carries no cached
 * display text. `XLSX.read` reconstructs `w` for every error cell a real file holds
 * (`{ t: 'e', v: 7 }` reads back as `w: '#DIV/0!'` — verified against xlsx 0.18.5),
 * so this is a fallback for a hand-built sheet, not a path a read file takes. It
 * exists so that path cannot put a raw BIFF byte on screen or into a
 * shared-backbone `device.version`: the value a cell reads as is always something
 * the Manager could have seen in their own spreadsheet.
 *
 * The set is closed — these are every code Excel defines — so an unmapped code means
 * a cell no spreadsheet produced. It reads `#N/A`, which keeps that guarantee (a
 * token the Manager could have seen, and never blank) where an invented `#ERROR!`
 * would break exactly the guarantee this table exists to make.
 */
const SHEET_ERROR_TEXT: Record<number, string> = {
  0x00: '#NULL!',
  0x07: '#DIV/0!',
  0x0f: '#VALUE!',
  0x17: '#REF!',
  0x1d: '#NAME?',
  0x24: '#NUM!',
  0x2a: '#N/A',
  0x2b: '#GETTING_DATA',
};

/** A header/cell value as displayed text: `''` for a blank, verbatim otherwise. */
function displayedText(value: unknown): string {
  return value === undefined || value === null ? '' : String(value);
}

/**
 * ONE spreadsheet cell as the text the Manager sees — the rule behind AD-23's
 * "a cell is the text the file displays".
 *
 * Reading `cell.w` (the formatted text) unconditionally is NOT that rule, because
 * the General format's rendering is itself lossy: xlsx 0.18.5 renders the stored
 * `123456789012` as `"1.23457E+11"` and `1/3` as `"0.333333333"`. Taking those
 * would trade the trailing-zero defect this story exists to fix for a new
 * precision defect on the same shared-backbone `device.version` field. So:
 *
 * - **Date cell:** its rendering, always. Dates are `t: 'd'` here because the read
 *   passes `cellDates: true` precisely so they never meet the numeric predicate
 *   below: a `yyyy`-formatted serial renders `2024`, which re-reads as a finite
 *   number differing from `45352`, so the predicate would call the rendering lossy
 *   and hand back the serial — the exact defect the Intent names. `m/d/yy` happens
 *   to re-read as `NaN` and survive either way, which is why only the whole date
 *   FAMILY being routed here is safe (`yyyy`, `yy`, `mm`, `d`, `yyyymmdd`, `h:mm`
 *   all render digit-only; verified against xlsx 0.18.5).
 * - **Numeric cell:** the displayed text, UNLESS that text re-reads as a finite
 *   number that differs from the stored value — proof that the rendering dropped
 *   information — in which case the exact stored value wins. A DECORATED format
 *   re-reads as `NaN` (`"9,074,729"`, `"50%"`) and is therefore kept: decoration is
 *   a presentation choice the Manager made, and keeping it is what makes `1.10` and
 *   `3.0` arrive as the sheet shows them. A blank rendering (a `;;;`-style hiding
 *   format) also yields to the stored value: a number must never read as empty.
 *
 *   KNOWN CONSEQUENCE, in the other direction: a rendering that drops information
 *   takes the stored-value branch, so the value then differs from what the sheet
 *   displays — the one case where the headline "the text the file displays" does not
 *   hold literally. Three shapes reach it, and the test suite pins each:
 *   1. an explicit ROUNDING format — `1.234` shown as `1.23` under `0.00`, `1.5`
 *      shown as `2` under `0` — reads as `1.234` / `1.5`;
 *   2. the GENERAL format's own rounding, which is not an authoring choice at all:
 *      Excel renders to ~11 significant digits, so a float artifact like
 *      `=1.1*3` (stored `3.3000000000000003`, shown `3.3`) reads as the stored
 *      value in full, and `1/3` (shown `0.333333333`) reads as
 *      `0.3333333333333333` — JavaScript's shortest round-trip form, which is a
 *      third representation: neither the sheet's rendering nor Excel's own 15-digit
 *      value;
 *   3. NOT a rounding format that is also DECORATED: `#,##0` on `1234.5` renders
 *      `1,235`, which re-reads as `NaN`, so decoration wins and the ROUNDED
 *      `1,235` is what arrives. The rule is about the rendering re-reading as a
 *      differing number, never about the format's intent — do not restate it as
 *      "a rounding format keeps the stored value".
 *
 *   Keeping the stored value is deliberate where it happens: a truncated version on
 *   a shared-backbone `device.version` is the silent loss this story exists to
 *   close, and the difference is not silent — the mapping step samples this same
 *   cell read, so the Manager sees `e.g. 1.234` before Continue. Logged in
 *   `deferred-work.md`.
 * - **Error cell** (`#DIV/0!`): its displayed error text, never `''` — a blank
 *   would silently yield to a lower-precedence duplicate column, and a row of
 *   only error cells would vanish. A cell with no cached text falls back to
 *   {@link SHEET_ERROR_TEXT}, never to the raw BIFF code.
 * - **Boolean:** `TRUE`/`FALSE`, which is what the sheet shows (logged in
 *   `deferred-work.md` with the other representation changes). Stated as a branch
 *   rather than left to the cached rendering, so the documented behaviour holds for a
 *   hand-built `{ t: 'b', v: true }` too — that cell has no `w` and would otherwise
 *   read JavaScript's lowercase `true`.
 * - **Everything else** (text): the displayed text, else the stored value.
 *
 * Do NOT "simplify" this back to `sheet_to_json(sheet, { raw: false })`: that is
 * exactly the unconditional-`w` read, and it also blanks error cells.
 */
function sheetCellText(cell: XLSX.CellObject | undefined): string {
  if (!cell) return '';
  const shown = cell.w;
  const stored = cell.v;
  if (cell.t === 'e') {
    if (shown !== undefined) return shown;
    // `v` is normally the BIFF code, but a hand-built cell may carry the error TEXT
    // itself - use it rather than overwriting a real error with a substitute.
    if (typeof stored === 'string' && stored.trim() !== '') return stored;
    return typeof stored === 'number' ? (SHEET_ERROR_TEXT[stored] ?? '#N/A') : '#N/A';
  }
  // A DATE cell is `t: 'd'` because the read passes `cellDates: true`, so it takes
  // the branch below and keeps its rendering whatever the format looks like. Do not
  // let it reach the numeric branch: `yyyy` renders `2024`, which re-reads as a
  // finite number differing from the serial, so the predicate would hand back
  // `45352` - this story's headline defect, on the same field.
  if (cell.t === 'd') return shown ?? isoDateText(stored);
  if (cell.t === 'b') return stored ? 'TRUE' : 'FALSE';
  if (cell.t === 'n' && typeof stored === 'number') {
    if (shown === undefined || shown.trim() === '') return String(stored);
    const reparsed = Number(shown);
    if (Number.isFinite(reparsed) && reparsed !== stored) return String(stored);
    return shown;
  }
  return shown ?? displayedText(stored);
}

/**
 * A header cell as text: its rendering, else the stored value — the `w ?? v` idiom
 * unconditionally, with NO lossy-format predicate.
 *
 * A header is a NAME, not a value: the Manager finds it by reading it off their
 * sheet, and every alias lookup and every display name resolves through it. So
 * fidelity to the rendering is the whole requirement and precision is beside the
 * point, which is the opposite trade from {@link sheetCellText}. Routing headers
 * through the value predicate named a `0.00`-formatted header cell `1.234` where the
 * sheet shows `1.23` — a column name present nowhere in the file, which is the class
 * of defect this story exists to close (verified against xlsx 0.18.5).
 */
function sheetHeaderText(cell: XLSX.CellObject | undefined): string {
  if (!cell) return '';
  return cell.w ?? (cell.t === 'd' ? isoDateText(cell.v) : displayedText(cell.v));
}

/** A `cellDates` Date with no cached rendering, as `YYYY-MM-DD` rather than a JS date string. */
function isoDateText(value: unknown): string {
  return value instanceof Date ? (value.toISOString().split('T')[0] ?? '') : displayedText(value);
}

/**
 * A worksheet read cell by cell over its used range: the header row (the range's
 * FIRST row) and one entry per row below it — `null` for a row the sheet holds no
 * cell for at all.
 *
 * Reading the range directly rather than through `sheet_to_json` is what lets
 * {@link sheetCellText} decide per cell (see its docblock). It also means this
 * function owns row presence: a row of empty-STRING cells is a row (it reaches
 * classification as a visible row error), while a row the sheet has no cell for is
 * not — which is what `sheet_to_json`'s `blankrows: false` did.
 *
 * One deliberate difference from object-mode `sheet_to_json`: when the used range
 * starts on a blank row (an artifact of deleting a title row), that row is this
 * sheet's header row, so its columns are unnamed-but-positioned rather than keyed
 * `__EMPTY`, `__EMPTY_1`, … Better on both counts, but not row-for-row parity.
 *
 * `firstColumn` is the range's own 0-based first column, which the caller needs to
 * turn a cell's index in `headers` into the column number the Manager reads off
 * their spreadsheet: a sheet whose stored `!ref` is `B1:D2` (column A empty — a
 * routine layout choice, and the offset survives a write/read round trip) reports
 * three headers whose real columns are B, C and D, i.e. 2, 3 and 4.
 */
function sheetGrid(sheet: XLSX.WorkSheet): {
  headers: string[];
  rows: (string[] | null)[];
  firstColumn: number;
} {
  const ref = sheet['!ref'];
  if (!ref) return { headers: [], rows: [], firstColumn: 0 };
  const range = XLSX.utils.decode_range(ref);
  const readRow = (
    r: number,
    read: (cell: XLSX.CellObject | undefined) => string,
  ): { cells: string[]; present: boolean } => {
    const cells: string[] = [];
    let present = false;
    for (let c = range.s.c; c <= range.e.c; c++) {
      const cell = sheet[XLSX.utils.encode_cell({ r, c })] as XLSX.CellObject | undefined;
      if (cell) present = true;
      cells.push(read(cell));
    }
    return { cells, present };
  };
  const rows: (string[] | null)[] = [];
  for (let r = range.s.r + 1; r <= range.e.r; r++) {
    const { cells, present } = readRow(r, sheetCellText);
    rows.push(present ? cells : null);
  }
  // The header row is read by {@link sheetHeaderText}, NOT by the value reader: a
  // header is a name to be found in the file, so its rendering is the whole
  // requirement and the value predicate's precision trade does not apply to it.
  return { headers: readRow(range.s.r, sheetHeaderText).cells, rows, firstColumn: range.s.c };
}

/**
 * JSON's column list: the union of the records' own keys, in first-seen order.
 *
 * This is the ONLY format whose columns come from record keys, because it is the
 * only one with no header row to allocate from — so `key === header` here and a
 * key is its own identity. `JSON.parse` has already applied `Object.keys`'
 * integer-like-keys-first ordering, so a JSON file with integer-like keys still
 * lists numerically; that is documented, unchanged behaviour.
 *
 * Deliberately NOT a general "order the columns" helper: CSV and XLSX allocate
 * their identities positionally at read time and never reconcile a header list
 * against output keys again (AD-23). Re-generalizing this function is how that
 * defect class comes back.
 */
function recordKeyColumns(records: readonly Record<string, unknown>[]): ImportColumn[] {
  const seen = new Set<string>();
  const columns: ImportColumn[] = [];
  for (const rec of records) {
    for (const key of Object.keys(rec)) {
      if (seen.has(key)) continue;
      seen.add(key);
      columns.push({ key, header: key, position: columns.length + 1 });
    }
  }
  return columns;
}

/**
 * Read a user-selected file (CSV/XLSX/JSON) into RAW records plus the columns
 * detected in it, dispatched by lower-cased extension. Nothing is resolved to a
 * canonical field here — that is {@link applyMapping}'s job — so the headers the
 * old parser threw away survive for the wizard's Map-columns step.
 *
 * Every whole-file rejection fires HERE: an unknown extension, an unparseable
 * file, the size cap, the row cap, the column cap, and zero data rows. A file the
 * platform will not accept must never reach a mapping UI.
 */
export async function readImportFile(file: File): Promise<ParsedImportFile> {
  const name = (file.name ?? '').toLowerCase();
  if (typeof file.size === 'number' && file.size > MAX_FILE_BYTES) {
    throw new Error(`The file is too large (limit ${Math.round(MAX_FILE_BYTES / (1024 * 1024))} MB).`);
  }
  let raw: unknown[];
  /**
   * The columns allocated by the branch, or `null` for JSON — the one format with
   * no header row, whose columns are derived from the record keys instead.
   */
  let allocated: ImportColumn[] | null = null;

  if (name.endsWith('.json')) {
    const text = await readText(file);
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      throw new Error('The .json file could not be parsed.');
    }
    if (Array.isArray(parsed)) {
      raw = parsed;
    } else if (parsed && typeof parsed === 'object' && Array.isArray((parsed as { rows?: unknown }).rows)) {
      raw = (parsed as { rows: unknown[] }).rows;
    } else {
      throw new Error('The .json file must be an array of rows or an object with a "rows" array.');
    }
  } else if (name.endsWith('.csv')) {
    const text = await readText(file);
    /**
     * The verbatim header text per physical column index, captured on FIRST
     * sight. The guard is LOAD-BEARING, not defensive: papaparse 5.5.4 invokes
     * `transformHeader` TWICE per column, and the second call is handed the name
     * the first call returned (observed: `[["Site Name",0],…,["c0",0],…]`). An
     * unguarded capture therefore records `c0`, `c1`, … and every column ends up
     * named after the parser's own synthetic key. A blank header records `''`,
     * which is not `undefined`, so it is captured too.
     *
     * A second `preview: 1` parse is NOT an alternative: papaparse auto-detects
     * the delimiter from the rows it sees, so a one-row preview can split
     * differently from the full read and silently mis-name every column.
     */
    const rawHeaders: string[] = [];
    const result = Papa.parse<Record<string, unknown>>(text, {
      header: true,
      skipEmptyLines: true,
      // `header: true` is kept precisely so papaparse's structural
      // TooFewFields/TooManyFields/quote errors keep firing (AR-19). The synthetic
      // keys are supplied through `transformHeader` instead of by post-processing
      // an array-mode parse, so no error is traded away for the new identity.
      transformHeader: (h, i) => {
        if (rawHeaders[i] === undefined) rawHeaders[i] = h;
        return columnKey(i);
      },
    });
    // Surface STRUCTURAL parse errors (ragged rows, unclosed quotes) instead of
    // silently importing misaligned data (AR-19 "never silently drop a row").
    // Ignore the benign `UndetectableDelimiter` warning fired on trivial/empty
    // input — that case falls through to the "no data rows" guard below.
    const structural = result.errors.filter((e) => e.code !== 'UndetectableDelimiter');
    if (structural.length > 0) {
      const first = structural[0]!;
      const where = typeof first.row === 'number' ? ` (row ${first.row + 1})` : '';
      throw new Error(`The .csv could not be parsed: ${first.message}${where}`);
    }
    raw = result.data;
    // One column per physical header cell, in file order: duplicates stay two
    // columns (papaparse no longer collapses them onto one key) and a blank one
    // keeps its position.
    allocated = Array.from({ length: rawHeaders.length }, (_, i) => ({
      key: columnKey(i),
      header: rawHeaders[i] ?? '',
      position: i + 1,
    }));
  } else if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
    const buf = await readArrayBuffer(file);
    // `cellDates` is load-bearing, not a convenience: it types a date cell `'d'` so
    // {@link sheetCellText} keeps its rendering for EVERY date format, including the
    // digit-only ones (`yyyy`, `mm`, `d`) whose rendering the value predicate would
    // otherwise judge lossy and replace with the raw serial. Every other cell type
    // reads back byte-identically with it on (verified against xlsx 0.18.5).
    const wb = XLSX.read(new Uint8Array(buf), { type: 'array', cellDates: true });
    if (wb.SheetNames.length === 0) throw new Error('The spreadsheet has no sheets.');
    /**
     * Workbook-level column registry: a MERGE KEY -> the column allocated for each
     * occurrence index of that key within a sheet.
     *
     * A NAMED column merges across sheets on its header, normalized the same way
     * every other comparison in this file normalizes one (trimmed + lower-cased),
     * so `Site Name` on one sheet and `site name ` on another are the one column
     * the Manager thinks they are - while a within-sheet `Version, Version` stays
     * two distinct columns (occurrence 0 and 1). Splitting a shared header would
     * change which rows carry a value, which is the data loss this story closes.
     *
     * A BLANK header NEVER merges across sheets: absence of a header is not
     * evidence of sameness, and fusing Sheet1's second column with Sheet2's first
     * would file one sheet's values under another sheet's column. Blank keys are
     * therefore scoped to their sheet. The `h:`/`b:` prefixes keep the two
     * namespaces apart without needing a separator no header may contain.
     */
    const registry = new Map<string, ImportColumn[]>();
    const columns: ImportColumn[] = [];
    /**
     * The next position to hand out, and how many sheets have contributed data.
     *
     * On the FIRST data sheet a column's position is its own spreadsheet column
     * number — `sheetGrid`'s `firstColumn` offset plus its index — so a sheet laid
     * out from column B labels its columns 2, 3, 4 and the `(column N)` the Manager
     * reads matches the column they can point at. Counting from the used range
     * instead was the SAME authoritative-looking-and-wrong label this story exists
     * to close, just reached by a different route (Story 8.3 review pass 2).
     *
     * A later sheet's NEW column cannot claim a spreadsheet column number — the
     * position is union order by then (AD-23 exception (ii)) — so it appends here,
     * which also keeps positions unique when the first sheet was offset.
     */
    let nextPosition = 1;
    let dataSheetsSeen = 0;
    // Concatenate rows across ALL sheets - a multi-sheet workbook must not
    // silently drop later sheets. Rows from non-data sheets surface as visible
    // row errors downstream (not a silent drop).
    const sheetRecords = wb.SheetNames.flatMap((sheetName, sheetIndex) => {
      const sheet = wb.Sheets[sheetName];
      if (!sheet) return [];
      // Read the used range CELL BY CELL, so every value arrives as the text the
      // Manager sees - `1.10`, `3.0`, `3/1/24` rather than `1.1`, `3`, `45352`,
      // which would otherwise land on a shared-backbone `device.version` - without
      // the new precision loss an unconditional formatted-text read introduces. The
      // per-cell rule, and why `sheet_to_json({ raw: false })` is NOT it, are in
      // {@link sheetCellText}.
      //
      // What this guarantees, exactly: fidelity to what the file DISPLAYS. `1.10`
      // typed into a General-formatted cell displays as `1.1` and is stored as
      // `1.1` - the trailing zero is not in the file and nothing here can recover
      // it. Do not chase that case. The cost of the same rule is that a DECORATED
      // format arrives decorated (a `#,##0` IMO reads `9,074,729` and fails
      // validation as a VISIBLE row error, a boolean reads `TRUE`, a `0%` cell
      // reads `50%`); the mapping step's sample shows the Manager the same string
      // first, and only a deliberately applied format changes anything.
      const { headers, rows, firstColumn } = sheetGrid(sheet);
      const dataRows = rows.filter((row): row is string[] => row !== null);
      // Only a sheet that produced DATA rows has a header row. A title, legend or
      // instructions tab (one row of prose, no data under it) contributes nothing:
      // registering its top row would invent a column present in no record,
      // inflate the "will not be imported" count, and let that non-column satisfy
      // the Site Name gate.
      if (headers.length === 0 || dataRows.length === 0) return [];
      dataSheetsSeen += 1;
      const isFirstDataSheet = dataSheetsSeen === 1;
      const occurrences = new Map<string, number>();
      const sheetColumns = headers.map((header, i) => {
        const mergeKey =
          header.trim() === '' ? `b:${sheetIndex}` : `h:${header.trim().toLowerCase()}`;
        const nth = occurrences.get(mergeKey) ?? 0;
        occurrences.set(mergeKey, nth + 1);
        const forKey = registry.get(mergeKey) ?? [];
        let column = forKey[nth];
        if (!column) {
          const position = isFirstDataSheet ? firstColumn + i + 1 : nextPosition;
          nextPosition = position + 1;
          column = { key: columnKey(columns.length), header, position };
          forKey[nth] = column;
          registry.set(mergeKey, forKey);
          columns.push(column);
        }
        return column;
      });
      return dataRows.map((row) => {
        // Prototype-free, and keyed by THIS SHEET's columns - never the workbook
        // union, whose order need not match this sheet's (Sheet2 headed
        // `Site Name, A` against a union of `A, Site Name` would file every value
        // under the wrong column).
        const rec = Object.create(null) as Record<string, unknown>;
        sheetColumns.forEach((column, i) => {
          rec[column.key] = row[i] ?? '';
        });
        return rec;
      });
    });
    raw = sheetRecords;
    // A stored `!ref` wider (or taller) than the sheet's content is a common
    // editing artifact, so a blank-headed column may exist in the range and hold
    // nothing at all. That is padding, not a column: listing it would show an
    // unmappable `(unnamed column)`, inflate the not-imported count and claim the
    // file has columns with no distinct header. A blank-headed column that DOES
    // carry a value on some row is a real column and stays (the "data row wider
    // than the header row" case). Surviving columns keep their TRUE positions, so a
    // dropped phantom leaves a gap rather than renumbering the file.
    allocated = columns.filter(
      (column) =>
        column.header.trim() !== '' || sheetRecords.some((rec) => cellText(rec, column.key) !== ''),
    );
  } else {
    throw new Error('Unsupported file type. Please upload a .csv, .xlsx, or .json file.');
  }

  // A non-object entry (a JSON array of scalars) carries no columns but is still
  // a row: it must reach classification as an empty row error, never vanish.
  const records: Record<string, unknown>[] = raw.map((r) =>
    r && typeof r === 'object' ? (r as Record<string, unknown>) : {},
  );
  if (records.length === 0) throw new Error('The file has no data rows.');
  if (records.length > MAX_ROWS) {
    throw new Error(`The file has too many rows (${records.length}); the limit is ${MAX_ROWS}.`);
  }

  const columns = allocated ?? recordKeyColumns(records);
  if (columns.length > MAX_COLUMNS) {
    throw new Error(
      `The file has too many columns (${columns.length}); the limit is ${MAX_COLUMNS}.`,
    );
  }

  return { columns, records };
}

/**
 * Read a column's target WITHOUT falling through to `Object.prototype`.
 *
 * STILL LOAD-BEARING after Story 8.3: CSV/XLSX column keys are synthetic, but a
 * JSON file's columns ARE its raw record keys ({@link ParsedImportFile.columns}),
 * so `__proto__`, `constructor` or `toString` can still be a column identity. A
 * plain-object lookup would then answer with an inherited value that is truthy
 * and is not an {@link ImportField} at all — enough to key a Map by
 * `Object.prototype`, emit an `"[object Object]"` row key, and make the wizard
 * announce that nothing is being left out while that column reaches nothing. The
 * wizard's `{ ...m, [key]: v }` spread also re-introduces `Object.prototype` on
 * every edit. Every read of a mapping and of a record goes through an
 * own-property check.
 */
export function mappingTarget(mapping: ImportColumnMapping, column: string): ImportField | '' {
  return Object.hasOwn(mapping, column) ? (mapping[column] ?? '') : '';
}

/** A cell value, or `''` when the record genuinely has no such own column. */
function cellText(record: Record<string, unknown>, column: string): string {
  if (!Object.hasOwn(record, column)) return '';
  const cell = record[column];
  return cell === undefined || cell === null ? '' : String(cell).trim();
}

/**
 * Suggest a mapping for the detected columns by resolving each column's HEADER
 * through {@link HEADER_ALIASES} (trimmed + lower-cased), `''` (ignore) when
 * unknown, keyed by {@link ImportColumn.key}. This is the ONLY remaining role of
 * the alias table: it proposes, the Manager disposes. An unrecognized header is no
 * longer discarded — it is suggested as ignored, listed in the wizard, and
 * repointable at any field.
 */
export function suggestMapping(columns: readonly ImportColumn[]): ImportColumnMapping {
  // Prototype-less: a JSON column identity may be `__proto__` (its columns are raw
  // record keys), and assigning it must create an own property rather than
  // silently invoke the inherited setter. (The wizard's `{ ...m, [key]: v }`
  // spread re-introduces Object.prototype, so reads are guarded too — see
  // `mappingTarget`.)
  const mapping = Object.create(null) as ImportColumnMapping;
  for (const column of columns) {
    const alias = column.header.trim().toLowerCase();
    mapping[column.key] = Object.hasOwn(HEADER_ALIASES, alias) ? (HEADER_ALIASES[alias] ?? '') : '';
  }
  return mapping;
}

/**
 * Invert a mapping into `field → the columns targeting it`, in the order they
 * are consulted: qualified-alias columns first, {@link WEAK_HEADER_ALIASES} ones
 * last, file order within each group. Fields nothing targets are absent.
 *
 * Story 7.1's precedence rule, generalized from per-cell to per-field: a weak
 * header (`Version`) is consulted only after every qualified one (`Device
 * Version`), whichever column the file lists first — but a BLANK qualified cell
 * still yields to the weak column, because the winner is the first NON-EMPTY
 * value, not the first column. Exported so the wizard discloses the same winner
 * it will actually get (one implementation, so the two cannot drift).
 *
 * The bucket is chosen on the column's HEADER (Story 8.3), never on a parser
 * token: two columns headed `Version` are BOTH weak, so neither can outrank the
 * other or a qualified `Device Version`. The old model let the parser's invented
 * `Version_1` bucket as *qualified* and beat the file's own weak column.
 */
export function mappedColumnsByField(
  columns: readonly ImportColumn[],
  mapping: ImportColumnMapping,
): Map<ImportField, ImportColumn[]> {
  const qualified = new Map<ImportField, ImportColumn[]>();
  const weak = new Map<ImportField, ImportColumn[]>();
  for (const column of columns) {
    const field = mappingTarget(mapping, column.key);
    if (!field) continue; // '' (ignore) or not mapped at all
    const bucket = WEAK_HEADER_ALIASES.has(column.header.trim().toLowerCase()) ? weak : qualified;
    const list = bucket.get(field);
    if (list) list.push(column);
    else bucket.set(field, [column]);
  }
  const byField = new Map<ImportField, ImportColumn[]>();
  for (const [field, cols] of qualified) byField.set(field, [...cols]);
  for (const [field, cols] of weak) {
    const list = byField.get(field);
    if (list) list.push(...cols);
    else byField.set(field, [...cols]);
  }
  return byField;
}

/**
 * Apply a CONFIRMED mapping to raw records → canonical {@link ImportRow}[].
 *
 * Per row and per target field, the value is the FIRST NON-EMPTY trimmed value
 * among the columns mapped to that field, consulted in
 * {@link mappedColumnsByField} order. A blank cell contributes nothing, so a
 * present-but-empty column never shadows one that has data. Flag/vessel casing
 * is canonicalized afterwards (the server's isFlagState/isVesselType demand the
 * contract's exact value; siteType is lower-cased at classify time).
 *
 * Columns mapped to `''`, and columns the mapping does not mention, are read
 * from nothing: after this call the raw headers are gone, so an unmapped column
 * has no back door into a row.
 */
export function applyMapping(parsed: ParsedImportFile, mapping: ImportColumnMapping): ImportRow[] {
  const byField = mappedColumnsByField(parsed.columns, mapping);
  return parsed.records.map((rec) => {
    const out: Partial<Record<ImportField, string>> = {};
    for (const [field, cols] of byField) {
      for (const column of cols) {
        const val = cellText(rec, column.key);
        if (val !== '') {
          out[field] = val;
          break;
        }
      }
    }
    if (out.flagState) out.flagState = FLAG_BY_LOWER.get(out.flagState.toLowerCase()) ?? out.flagState;
    if (out.vesselType) out.vesselType = VESSEL_BY_LOWER.get(out.vesselType.toLowerCase()) ?? out.vesselType;
    return out as ImportRow;
  });
}

/** How much of a cell the mapping step shows — enough to recognize a column. */
const SAMPLE_MAX_CHARS = 40;

/**
 * Cut a sample to {@link SAMPLE_MAX_CHARS} on a CODE POINT boundary. `slice` cuts
 * UTF-16 code units, so a cell whose 40th unit is the high half of a surrogate
 * pair (an emoji, a CJK extension character) would end in a lone surrogate and
 * render as a replacement glyph.
 */
export function elide(val: string): string {
  const points = [...val];
  return points.length > SAMPLE_MAX_CHARS
    ? `${points.slice(0, SAMPLE_MAX_CHARS).join('')}…`
    : val;
}

/**
 * The first non-empty value of EVERY column, in one sweep (columns drop out as
 * they are filled, so a normal file settles on its first row), keyed by
 * {@link ImportColumn.key}. Computed once per read file rather than per column per
 * render — the alternative re-scans up to {@link MAX_ROWS} records for every
 * column on every keystroke in the mapping step. Long free-text cells are elided
 * so one of them cannot blow out a row.
 *
 * The sample comes from the same cell read {@link applyMapping} uses, so what the
 * Manager checks before Continue is the value that will reach the row — including a
 * decorated XLSX number (`9,074,729`), which is what makes reading displayed text a
 * visible trade rather than a silent one. It is not always the IDENTICAL string:
 * this is the first non-empty value across the file (not this row's), it is
 * trimmed, and a cell longer than {@link SAMPLE_MAX_CHARS} is elided with a `…`.
 * So it discloses the FORM a value takes, not the exact bytes of every cell.
 */
export function columnSamples(parsed: ParsedImportFile): Map<string, string> {
  const samples = new Map<string, string>();
  // Keyed by `column.key`, never by the ImportColumn INSTANCE: a caller that maps
  // over `columns` to copy them (`columns.map((c) => ({ ...c }))`) would otherwise
  // leave every column unmatched here, and a column with no sample renders as "no
  // values in this column" — the false claim this story just removed for `__proto__`.
  const remaining = new Map(parsed.columns.map((column) => [column.key, column]));
  for (const rec of parsed.records) {
    if (remaining.size === 0) break;
    for (const column of [...remaining.values()]) {
      const val = cellText(rec, column.key);
      if (val === '') continue;
      samples.set(column.key, elide(val));
      remaining.delete(column.key);
    }
  }
  return samples;
}

/**
 * The default parse path: `read → suggest → apply`, i.e. exactly what the wizard
 * does when the Manager continues without touching a mapping control. Kept as a
 * one-liner so every pre-existing parse test still exercises the new primitives
 * and pins that a normal file imports precisely as it did before Story 7.4.
 */
export async function parseImportFile(file: File): Promise<ImportRow[]> {
  const parsed = await readImportFile(file);
  return applyMapping(parsed, suggestMapping(parsed.columns));
}

// ---- Classification -------------------------------------------------------

export type MatchKind = 'matched' | 'unmatched' | 'ambiguous';

export interface ClassifiedVendor {
  /** Lower-cased trimmed name — the dedup/reference key. */
  key: string;
  name: string;
  country?: string;
  category?: string;
  match: MatchKind;
  /** Set when `matched`. */
  existingId?: string;
  /** Set when `ambiguous` (the visible same-name candidates). */
  candidates?: { id: string; name: string }[];
}

/**
 * A device's OPTIONAL attributes — the ones AD-24 governs. Never part of the identity
 * key (that is vendor + model), which is why two rows can disagree about one of them.
 */
export type DeviceAttribute = 'version' | 'category';

/**
 * How many distinct losing values one attribute's conflict discloses by name before it
 * starts counting. Small on purpose: the disclosure exists so the Manager can see the
 * file contradicted itself and go fix the file — reading out 4,999 revisions serves
 * nobody, and the count says how many were held back.
 *
 * **It now bounds TWO different things, and raising it is not a cosmetic change.** For a
 * Device it caps a DISCLOSURE — the losing values are named and the winner is already
 * decided. For a Site (DEV-4444) it caps a CHOICE: these are the values the Manager may
 * select between for a vessel's legal identity, so a larger number here widens what can
 * be committed as an IMO, and a smaller one widens the set the Manager must go and fix
 * the file to reach. Change it with both consumers in mind, or split it in two.
 */
const MAX_CONFLICT_VALUES = 5;

export interface ClassifiedDevice {
  /** `${vendorKey}::${lower(model)}` — the dedup/reference key. */
  key: string;
  model: string;
  /**
   * Free-text category. Merges by AD-24's rule — first non-empty in file order —
   * exactly like {@link version}. Story 8.4 changed this: it used to be taken from
   * whichever row happened to CREATE the device, so a sheet whose first row left
   * the category blank and whose second supplied it lost the value, while the
   * identical shape for `version` did not. Two sibling optional attributes on one
   * entity, two undocumented rules; now one rule, recorded as AD-24 and applied on
   * the server too (`import.repository.ts`'s plan-wide pre-pass).
   */
  category?: string;
  /**
   * Hardware/firmware revision (Story 7.1). Deliberately NOT part of `key`:
   * device identity is vendor+model, so two rows naming the same vendor+model
   * with different versions collapse into ONE device and the FIRST non-empty
   * version wins (AD-24).
   */
  version?: string;
  /**
   * The non-empty values AD-24's first-non-empty rule DISCARDED, per attribute, in
   * file order and de-duplicated — absent entirely when the file never contradicted
   * itself (Story 8.4).
   *
   * The collapse itself stays: two rows naming one vendor + model are one device,
   * because that is device identity. What must not stay is the silence — the review
   * step names the loser beside the winner, whatever the device's match state,
   * because the file contradicted itself either way and the Manager is the only one
   * who can resolve it. This is NOT a row error: nothing was excluded.
   *
   * `values` is CAPPED at {@link MAX_CONFLICT_VALUES} and `more` counts the distinct
   * losers past the cap, because the number of losers is bounded only by `MAX_ROWS`
   * (5,000): one vendor + model repeated across a whole export with a per-unit
   * revision in the column — the shape Story 8.4 made far more reachable by
   * auto-suggesting `Rev`/`FW`/`Firmware` — would otherwise render every distinct
   * value in one line at the review step. Like every other cap on this story's
   * surfaces, the overflow is DISCLOSED rather than dropped.
   */
  conflicts?: Partial<Record<DeviceAttribute, { values: string[]; more: number }>>;
  /** References {@link ClassifiedVendor.key} of the manufacturer. */
  vendorKey: string;
  match: MatchKind;
  existingId?: string;
  candidates?: { id: string; model: string }[];
}

/**
 * A Site's type-specific attributes — the ones DEV-4444 governs. Never part of the
 * identity key (that is `lower(name)::type`), which is why two rows can disagree
 * about one of them.
 *
 * Unlike a device's `version`/`category`, EVERY one of these is REQUIRED by
 * {@link validateImportRow} for its site type, so a row that reaches classification
 * always carries a non-empty value. AD-24's "first non-empty" therefore degenerates
 * to "first", and any difference is a genuine contradiction rather than a later row
 * filling a blank the earlier one left.
 */
export type SiteAttribute = 'imo' | 'flagState' | 'vesselType' | 'city';

/**
 * How a surface NAMES each attribute, owned here so the wizard cannot drift from the
 * wording {@link validateImportRow}'s reasons already use ("ship is missing a flag
 * state", "vessel type … is not a recognised value"). One owner, no second copy —
 * a view that needs the label is not thereby its owner.
 */
export const SITE_ATTRIBUTE_LABELS: Readonly<Record<SiteAttribute, string>> = {
  imo: 'IMO',
  flagState: 'flag state',
  vesselType: 'vessel type',
  city: 'city',
};

/**
 * The attributes that belong to a Site of each type — see {@link mergeSiteAttribute}.
 *
 * Exported because it is also the STABLE ORDER a surface lists conflicts in. A
 * consumer must not derive that order from `Object.keys(site.conflicts)`, which is
 * insertion order and therefore depends on which attribute the file happened to
 * contradict first.
 */
export const SITE_ATTRIBUTES_BY_TYPE: Readonly<Record<SiteType, readonly SiteAttribute[]>> = {
  ship: ['imo', 'flagState', 'vesselType'],
  office: ['city'],
};

export interface ClassifiedSite {
  /** `${lower(name)}::${type}` — the reference key. */
  key: string;
  name: string;
  type: SiteType;
  /** The create payload when `unmatched`; absent when `matched`. */
  create?: CreateSiteRequest;
  match: Exclude<MatchKind, 'ambiguous'>;
  existingId?: string;
  /**
   * What the file said about this Site's attributes when it CONTRADICTED itself
   * (DEV-4444) — absent entirely when it did not.
   *
   * The collapse itself stays: two rows naming one name+type are one Site, because
   * that is Site identity (AD-24 clause 1's reasoning). What must not stay is the
   * silence — before this, `imo`, `flagState`, `vesselType` and `city` were read
   * ONLY on the row that first created the Site and every later row's differing
   * value was dropped with no row error and nothing rendered. An IMO is a vessel's
   * legal identity; the wrong one silently attaches every certificate to the wrong
   * hull.
   *
   * **Why this record carries its own `won` when {@link ClassifiedDevice.conflicts}
   * does not.** A device's winner survives as `device[attribute]`. A Site's winner
   * lives in {@link create}, which `classifyImport` DELETES once the Site matches an
   * existing one — so a losers-only record would leave a matched Site's disclosure
   * unable to name the value that won. Do not "simplify" this field by dropping
   * `won` and reading it back off `create`; the matched branch is exactly where the
   * disclosure is still needed and `create` is exactly where it is gone.
   *
   * `values` is CAPPED at {@link MAX_CONFLICT_VALUES} and `more` counts the distinct
   * losers past the cap. Unlike the device disclosure this cap is also a bound on
   * what the Manager can CHOOSE, so a surface offering the choice must say that
   * further values existed and were not offered — otherwise a capped list reads as
   * the complete one and the right value looks absent from the file.
   */
  conflicts?: Partial<Record<SiteAttribute, { won: string; values: string[]; more: number }>>;
  /** Keys of the distinct vendors/devices attached to this Site. */
  vendorKeys: string[];
  deviceKeys: string[];
}

/** A row excluded from import, with the human reason (never silently dropped). */
export interface RowError {
  /** 1-based row number in the parsed file. */
  row: number;
  siteName: string;
  reason: string;
}

/**
 * WHICH check a {@link RowIssue} came from — a stable identity, independent of the
 * rendered wording.
 *
 * Load-bearing for any surface that AGGREGATES issues. A not-recognised reason quotes
 * the offending value, so the reason STRING is per-value: five rows carrying five bad
 * flag codes are five distinct strings, and a 500-row export whose `Flag` column holds
 * ISO codes would render up to 500 report lines (bounded only by `MAX_ROWS`) — on the
 * single most likely real file, which is exactly the shape that failed the Epic 7
 * close-out UAT. Grouping on `check` instead makes the report one line per FAMILY, with
 * the offending values sampled. Never group on `reason`.
 */
export type RowCheck =
  | 'site-name-missing'
  | 'site-type-missing'
  | 'site-type-unrecognised'
  | 'imo-missing'
  | 'imo-malformed'
  | 'flag-state-missing'
  | 'flag-state-unrecognised'
  | 'vessel-type-missing'
  | 'vessel-type-unrecognised'
  | 'city-missing'
  | 'device-without-vendor';

/**
 * A CLOSED set of accepted values, with the name a report calls it by.
 *
 * The name travels with the values so a surface can render the list ONCE — "accepted
 * flag states: …" beside a report covering many rows — rather than repeating 17 values
 * on every line. Each vocabulary is a single module-level instance, so a consumer can
 * de-duplicate by identity.
 */
export interface RowVocabulary {
  /** Plural, lower-case, for "accepted {name}: …". */
  name: string;
  values: readonly string[];
}

export const SITE_TYPE_VOCABULARY: RowVocabulary = { name: 'site types', values: SITE_TYPES };
export const FLAG_STATE_VOCABULARY: RowVocabulary = { name: 'flag states', values: FLAG_STATES };
export const VESSEL_TYPE_VOCABULARY: RowVocabulary = { name: 'vessel types', values: VESSEL_TYPES };

/**
 * ONE reason a row cannot be imported (Story 8.4).
 *
 * `vocabulary` travels WITH the reason rather than being looked up beside it, so a
 * surface reporting the reason cannot state a list that has drifted from the list the
 * check actually used. Every closed-vocabulary check carries it — the MISSING branches
 * too, because an unmapped `Flag` column is the commonest way to reach one and the
 * Manager needs the list just as much. It is absent where there is no closed set at
 * all: a missing site name, an office with no city, an IMO of the wrong shape (a
 * FORMAT, which the reason states itself).
 */
export interface RowIssue {
  check: RowCheck;
  reason: string;
  /**
   * The offending cell value, when the reason quotes one — so an aggregating surface
   * can sample the values it saw without re-parsing the reason string.
   */
  value?: string;
  vocabulary?: RowVocabulary;
}

/**
 * THE row validator: every reason one {@link ImportRow} cannot be imported, in a
 * stable order (Story 8.4 AC1).
 *
 * This is the single implementation of "would this row survive". `classifyImport`
 * calls it to build its {@link RowError}s, and the wizard's Map-columns step calls
 * it — on `applyMapping`'s output, before Continue — to report how many rows a
 * mapping would exclude and why. There is deliberately no second copy: the mapping
 * step and the review step must never be able to disagree about the same row, which
 * is the defect class Epic 8 exists to close, and a test drives one fixture through
 * both surfaces asserting the reason string is identical.
 *
 * It needs NO Directory data — no vendors, no devices, no sites. That is what makes
 * the mapping-step preflight local and free: the whole report is computable before
 * the three `GET`s that Continue triggers, which is the point of moving it there.
 *
 * **PRECONDITION — it validates {@link applyMapping} OUTPUT, not raw cells.** Flag
 * state and vessel type are matched against the contract's EXACT values, and the
 * lower-case→canonical fold (`FLAG_BY_LOWER` / `VESSEL_BY_LOWER`) lives in
 * `applyMapping`, deliberately outside this function. So a hand-built row carrying
 * `panama` is reported as `flag state "panama" is not a recognised value` **with
 * `Panama` in the accepted list** — a report that contradicts itself. That is a
 * misuse, not a bug to fix here: relaxing the check would let a non-canonical value
 * reach `site.create` and be rejected by the server's `isFlagState` at commit, which
 * is the same "two surfaces disagree" failure one layer later. (Site type is folded
 * INSIDE, because `classifyImport` lower-cases it itself and the contract's values
 * are already lower-case.) Every caller must therefore parse through `applyMapping`
 * first; `import-parse.test.ts` pins that contract so a third caller cannot rediscover
 * it the hard way.
 *
 * On wording: a reason distinguishes an EMPTY cell from a PRESENT-BUT-UNRECOGNISED
 * one, and quotes the offending value in the second case. The pre-8.4 wording told
 * the Manager a flag state (or vessel type) was *missing* from a cell that plainly
 * contained `GR` — a false statement, and one they could not act on: they check the
 * column is mapped, see a value, and have nothing left to go on. Story 8.4 puts
 * these strings on a second surface, so the correction lands here, at the one
 * validator, rather than being propagated.
 */
export function validateImportRow(row: ImportRow): RowIssue[] {
  const siteName = (row.siteName ?? '').trim();
  const siteType = (row.siteType ?? '').trim();
  const imo = (row.imo ?? '').trim();
  const flagState = (row.flagState ?? '').trim();
  const vesselType = (row.vesselType ?? '').trim();
  const city = (row.city ?? '').trim();
  const vendorName = (row.vendorName ?? '').trim();
  const deviceModel = (row.deviceModel ?? '').trim();

  // The quoted value is ELIDED — validation still runs on the full cell, only the
  // rendered form is cut. A cell is arbitrary text (nothing upstream caps its length)
  // and Story 8.4 puts the offending value on two surfaces: inside the row error at
  // review, and inside the mapping step's report, where it is also sampled per family.
  // A 2,000-character free-text note mis-mapped to `Flag` would otherwise be quoted in
  // full on the one surface whose whole claim is being readable — the same unbounded-
  // render failure that aggregating per check rather than per reason string exists to
  // prevent. `elide` is the same code-point-safe cut the column samples use.
  //
  // The elided form is what travels as `RowIssue.value` too, so a report de-duplicates
  // its sampled values on what it will DISPLAY: two over-length values sharing a prefix
  // render as one line, never as two identical-looking ones. No accepted value comes
  // near the cut, so eliding can never make an off-list value read as a recognised one.
  const q = (val: string): string => elide(val);

  const issues: RowIssue[] = [];
  if (!siteName) issues.push({ check: 'site-name-missing', reason: 'missing site name' });
  if (!siteType) {
    // The blank case keeps its exact pre-8.4 wording: it never said "missing", it
    // states the closed vocabulary outright, and it is the string the story's own
    // I/O matrix names for an unmapped `Kind` column.
    issues.push({
      check: 'site-type-missing',
      reason: 'site type must be "ship" or "office"',
      vocabulary: SITE_TYPE_VOCABULARY,
    });
  } else if (!(SITE_TYPES as readonly string[]).includes(siteType.toLowerCase())) {
    issues.push({
      check: 'site-type-unrecognised',
      reason: `site type "${q(siteType)}" is not a recognised value`,
      value: q(siteType),
      vocabulary: SITE_TYPE_VOCABULARY,
    });
  } else if (siteType.toLowerCase() === 'ship') {
    if (!imo) issues.push({ check: 'imo-missing', reason: 'ship is missing an IMO number' });
    else if (!IMO_RE.test(imo)) {
      issues.push({
        check: 'imo-malformed',
        reason: `IMO "${q(imo)}" is not a 7-digit number`,
        value: q(imo),
      });
    }
    if (!flagState) {
      issues.push({
        check: 'flag-state-missing',
        reason: 'ship is missing a flag state',
        vocabulary: FLAG_STATE_VOCABULARY,
      });
    } else if (!(FLAG_STATES as readonly string[]).includes(flagState)) {
      issues.push({
        check: 'flag-state-unrecognised',
        reason: `flag state "${q(flagState)}" is not a recognised value`,
        value: q(flagState),
        vocabulary: FLAG_STATE_VOCABULARY,
      });
    }
    if (!vesselType) {
      issues.push({
        check: 'vessel-type-missing',
        reason: 'ship is missing a vessel type',
        vocabulary: VESSEL_TYPE_VOCABULARY,
      });
    } else if (!(VESSEL_TYPES as readonly string[]).includes(vesselType)) {
      issues.push({
        check: 'vessel-type-unrecognised',
        reason: `vessel type "${q(vesselType)}" is not a recognised value`,
        value: q(vesselType),
        vocabulary: VESSEL_TYPE_VOCABULARY,
      });
    }
  } else if (!city) {
    issues.push({ check: 'city-missing', reason: 'office is missing a city' });
  }
  if (deviceModel && !vendorName) {
    issues.push({ check: 'device-without-vendor', reason: 'a device model needs a vendor name' });
  }
  return issues;
}

/**
 * Merge one OPTIONAL device attribute by AD-24's rule — first non-empty in file
 * order — recording every distinct non-empty value that loses.
 *
 * `version` and `category` are two sibling optional attributes on the same entity,
 * and before Story 8.4 they merged by two different undocumented rules (`version`
 * first-non-empty, `category` from whichever row created the device). One rule,
 * stated once, applied here for both — and on the server for both, in
 * `import.repository.ts`'s plan-wide pre-pass.
 *
 * A blank cell is ABSENCE, not an assertion of emptiness, so it neither wins nor
 * counts as a conflict. A repeat of the winning value is not a conflict either: the
 * file did not contradict itself.
 *
 * Comparison is CASE-INSENSITIVE, matching how identity is compared everywhere else in
 * this module (`vendorKey` is `lower(name)`, the device key is `lower(model)`). `Radar`
 * followed by `radar` is one value spelled two ways, not a contradiction, and reporting
 * it as one would put a disclosure the Manager cannot act on next to every sloppily
 * cased export. The WINNER keeps its verbatim first spelling.
 *
 * `seen` carries the distinct losers already recorded, per device+attribute, so the
 * de-duplication stays O(1) per row and survives the display cap: counting the
 * overflow needs to know a value is new even once it is no longer being stored.
 */
function mergeDeviceAttribute(
  device: ClassifiedDevice,
  attribute: DeviceAttribute,
  value: string,
  seen: Map<string, Set<string>>,
): void {
  if (!value) return;
  const won = device[attribute];
  if (!won) {
    device[attribute] = value;
    return;
  }
  const folded = value.toLowerCase();
  if (won.toLowerCase() === folded) return;
  const seenKey = `${device.key}::${attribute}`;
  const already = seen.get(seenKey) ?? new Set<string>();
  seen.set(seenKey, already);
  if (already.has(folded)) return;
  already.add(folded);
  const conflicts = (device.conflicts ??= {});
  const record = (conflicts[attribute] ??= { values: [], more: 0 });
  if (record.values.length < MAX_CONFLICT_VALUES) record.values.push(value);
  else record.more += 1;
}

/**
 * Merge one of a Site's type-specific attributes, recording every distinct non-empty
 * value that loses (DEV-4444). The Site twin of {@link mergeDeviceAttribute}, and
 * deliberately the same shape so the two cannot drift.
 *
 * It differs from the device helper in exactly two ways, both forced:
 *
 * 1. **The loser record carries the winner** (see {@link ClassifiedSite.conflicts}),
 *    because a Site's winner is deleted from {@link ClassifiedSite.create} the moment
 *    the Site matches an existing one, while a device's survives on the device.
 * 2. **It is called only for the attributes of THAT Site's type**
 *    ({@link SITE_ATTRIBUTES_BY_TYPE}). A ship row may legitimately carry a City cell
 *    — `7-4-happy-path.csv` does — but a ship's `create` payload has no `city`, so
 *    merging all four unconditionally would raise a conflict on a field that is
 *    never written and can never be resolved.
 *
 * Everything else follows AD-24 verbatim: a blank cell is ABSENCE rather than an
 * assertion of emptiness, comparison is CASE-INSENSITIVE (identity is compared that
 * way everywhere else in this module, so `Piraeus` followed by `piraeus` is one value
 * spelled two ways, not a contradiction), the winner keeps its verbatim first
 * spelling, and `seen` carries the distinct losers already recorded per site+attribute
 * so the de-duplication stays O(1) per row and the overflow COUNT stays accurate past
 * the display cap.
 */
function mergeSiteAttribute(
  site: ClassifiedSite,
  attribute: SiteAttribute,
  value: string,
  seen: Map<string, Set<string>>,
): void {
  if (!value) return;
  // Type narrowing, not a runtime case: this runs inside the row loop, which is over
  // before the pass that deletes `create` from a matched Site.
  const create = site.create;
  if (!create) return;
  const won = create[attribute];
  // There is deliberately NO backfill arm here, and that is the one place this helper
  // does NOT mirror `mergeDeviceAttribute`. Every attribute it is called with is
  // REQUIRED by `validateImportRow` for that Site's type, so the row that built
  // `create` cannot have left one blank and "first non-empty fills a gap" would be
  // unreachable. An earlier draft kept one anyway, for symmetry — but it could only
  // become reachable if validation were loosened, which is precisely the world in
  // which its unchecked `as FlagState` / `as VesselType` casts would stop being
  // sound. Unreachable code that is wrong when it becomes reachable is worse than no
  // code; if an attribute here ever becomes optional, write the arm then, with the
  // revalidation that world requires.
  if (!won) return;
  const folded = value.toLowerCase();
  if (won.toLowerCase() === folded) return;
  const seenKey = `${site.key}::${attribute}`;
  const already = seen.get(seenKey) ?? new Set<string>();
  seen.set(seenKey, already);
  if (already.has(folded)) return;
  already.add(folded);
  const conflicts = (site.conflicts ??= {});
  const record = (conflicts[attribute] ??= { won, values: [], more: 0 });
  if (record.values.length < MAX_CONFLICT_VALUES) record.values.push(value);
  else record.more += 1;
}

export interface ImportClassification {
  sites: ClassifiedSite[];
  vendors: ClassifiedVendor[];
  devices: ClassifiedDevice[];
  rowErrors: RowError[];
}

/**
 * Classify parsed rows against the caller's visible Directory (exact,
 * case-insensitive on trimmed keys). Distinct Sites (name+type), Vendors (name),
 * and Devices (vendor+model) each become matched (link an existing id),
 * unmatched (create/propose), or ambiguous (>1 visible candidate — the Manager
 * must pick). Rows with validation errors are collected as {@link RowError}s and
 * excluded (never dropped without a trace); valid rows still classify.
 */
export function classifyImport(
  rows: ImportRow[],
  vendors: VendorDirectoryEntry[],
  devices: DeviceCatalogEntry[],
  sites: Site[],
): ImportClassification {
  const rowErrors: RowError[] = [];
  const siteMap = new Map<string, ClassifiedSite>();
  const vendorMap = new Map<string, ClassifiedVendor>();
  const deviceMap = new Map<string, ClassifiedDevice>();
  // `${deviceKey}::${attribute}` → the folded losers already recorded. Lives out here
  // rather than on the device so the display cap cannot cost the overflow COUNT its
  // accuracy (see {@link mergeDeviceAttribute}).
  const conflictsSeen = new Map<string, Set<string>>();
  // The Site twin of `conflictsSeen`, keyed `${site.key}::${attribute}` (DEV-4444).
  // Kept separate from the device map rather than sharing one: the two key spaces
  // cannot collide today, but one map serving two entity types is a coincidence to
  // maintain rather than a rule to read.
  const siteConflictsSeen = new Map<string, Set<string>>();

  rows.forEach((row, i) => {
    const rowNum = i + 1;
    const siteName = (row.siteName ?? '').trim();
    const siteType = (row.siteType ?? '').trim().toLowerCase();
    const imo = (row.imo ?? '').trim();
    const flagState = (row.flagState ?? '').trim();
    const vesselType = (row.vesselType ?? '').trim();
    const city = (row.city ?? '').trim();
    const vendorName = (row.vendorName ?? '').trim();
    const deviceModel = (row.deviceModel ?? '').trim();

    // ONE validator, shared with the mapping step's preflight (Story 8.4): the
    // reasons are not computed here, so the two surfaces cannot disagree about the
    // same row. Row validation needs no Directory data, which is what lets the
    // mapping step run this before fetching anything.
    const issues = validateImportRow(row);
    if (issues.length > 0) {
      rowErrors.push({ row: rowNum, siteName, reason: issues.map((i) => i.reason).join('; ') });
      return;
    }

    // Register the valid row's distinct Site.
    const type = siteType as SiteType;
    const siteKey = `${siteName.toLowerCase()}::${type}`;
    let site = siteMap.get(siteKey);
    if (!site) {
      const create: CreateSiteRequest =
        type === 'ship'
          ? { name: siteName, type: 'ship', imo, flagState: flagState as FlagState, vesselType: vesselType as VesselType }
          : { name: siteName, type: 'office', city };
      site = { key: siteKey, name: siteName, type, create, match: 'unmatched', vendorKeys: [], deviceKeys: [] };
      siteMap.set(siteKey, site);
    } else {
      // DEV-4444: the row is a REPEAT of a Site an earlier row already registered.
      // Before this arm existed the branch above was the only place a Site's
      // attributes were read, so everything this row said about the vessel was
      // dropped on the floor — no row error, no record, nothing rendered. Two rows
      // naming `MV Aurora` with different IMOs collapsed to one Site carrying the
      // first, and the discarded legal identity existed nowhere.
      //
      // Only the attributes belonging to THIS Site's type are merged; see
      // `mergeSiteAttribute` for why a ship's City cell must not become a conflict.
      const values: Record<SiteAttribute, string> = { imo, flagState, vesselType, city };
      for (const attribute of SITE_ATTRIBUTES_BY_TYPE[type]) {
        mergeSiteAttribute(site, attribute, values[attribute], siteConflictsSeen);
      }
    }

    let vendorKey: string | undefined;
    if (vendorName) {
      vendorKey = vendorName.toLowerCase();
      if (!vendorMap.has(vendorKey)) {
        const country = (row.vendorCountry ?? '').trim();
        const category = (row.vendorCategory ?? '').trim();
        vendorMap.set(vendorKey, {
          key: vendorKey,
          name: vendorName,
          ...(country ? { country } : {}),
          ...(category ? { category } : {}),
          match: 'unmatched',
        });
      }
      if (!site.vendorKeys.includes(vendorKey)) site.vendorKeys.push(vendorKey);
    }

    // A device-version cell on a row with no device model has nothing to attach to
    // and is ignored — this block only runs for a row that actually names a device.
    if (deviceModel && vendorKey) {
      // Story 7.1: `version` is deliberately ABSENT from the key. Device identity
      // is vendor+model, so `Pump-X / v1` and `Pump-X / v2` are ONE device.
      const deviceKey = `${vendorKey}::${deviceModel.toLowerCase()}`;
      const version = (row.deviceVersion ?? '').trim();
      const category = (row.deviceCategory ?? '').trim();
      const known = deviceMap.get(deviceKey);
      if (!known) {
        deviceMap.set(deviceKey, {
          key: deviceKey,
          model: deviceModel,
          vendorKey,
          ...(category ? { category } : {}),
          ...(version ? { version } : {}),
          match: 'unmatched',
        });
      } else {
        // AD-24: `version` and `category` merge by ONE rule — first non-empty, in
        // file order — and a value that loses is recorded rather than discarded
        // silently. A later row only fills what the earlier rows left blank.
        mergeDeviceAttribute(known, 'version', version, conflictsSeen);
        mergeDeviceAttribute(known, 'category', category, conflictsSeen);
      }
      if (!site.deviceKeys.includes(deviceKey)) site.deviceKeys.push(deviceKey);
    }
  });

  // Classify vendors: exact ci match on trimmed name.
  for (const v of vendorMap.values()) {
    const matches = vendors.filter((e) => e.vendor.name.trim().toLowerCase() === v.key);
    if (matches.length === 1) {
      v.match = 'matched';
      v.existingId = matches[0]!.vendor.id;
    } else if (matches.length > 1) {
      v.match = 'ambiguous';
      v.candidates = matches.map((e) => ({ id: e.vendor.id, name: e.vendor.name }));
    }
  }

  // Classify sites: exact ci match on name + exact type (link an existing Site).
  for (const s of siteMap.values()) {
    const match = sites.find(
      (site) => site.name.trim().toLowerCase() === s.name.toLowerCase() && site.type === s.type,
    );
    if (match) {
      s.match = 'matched';
      s.existingId = match.id;
      delete s.create;
    }
  }

  // Resolve each device's surviving category onto the closed DEVICE_CATEGORIES list.
  //
  // Deliberately AFTER the AD-24 merge above, not at the cell where the value is read.
  // The merge and its conflict disclosure operate on what the FILE said, so a sheet that
  // names 'Radar' on one row and 'Pumps' on another still reports that it contradicted
  // itself — both of those normalize to `Other`, and folding them first would silently
  // erase the contradiction the Manager needs to go and fix.
  //
  // Normalizing here rather than at the commit boundary is what keeps the wizard honest:
  // the preview renders this value, so what is reviewed is what is sent. Doing it in
  // `ImportWizard` instead would show 'Radar' and commit 'Other'.
  for (const d of deviceMap.values()) {
    const resolved = normalizeDeviceCategory(d.category);
    if (resolved === undefined) delete d.category;
    else d.category = resolved;
  }

  // Classify devices: exact ci match on model, scoped to the resolved (matched)
  // manufacturer. If the manufacturer is not a unique existing Vendor, the device
  // cannot match an existing row → it must be proposed.
  for (const d of deviceMap.values()) {
    const vendor = vendorMap.get(d.vendorKey);
    const model = d.model.trim().toLowerCase();
    if (vendor?.match === 'matched' && vendor.existingId) {
      const matches = devices.filter(
        (e) => e.device.model.trim().toLowerCase() === model && e.device.vendorId === vendor.existingId,
      );
      if (matches.length === 1) {
        d.match = 'matched';
        d.existingId = matches[0]!.device.id;
      } else if (matches.length > 1) {
        d.match = 'ambiguous';
        d.candidates = matches.map((e) => ({ id: e.device.id, model: e.device.model }));
      }
    }
  }

  return {
    sites: [...siteMap.values()],
    vendors: [...vendorMap.values()],
    devices: [...deviceMap.values()],
    rowErrors,
  };
}
